import { type Model } from "@binale-tech/shared";
import { Category } from "@domain/models/Category";

/** UStVA Kennzahlen computed from tax account balances instead of the BU key (FIBU-992). */
export type VatAccountRoute = "66" | "61" | "62" | "67" | "64" | "65" | "69" | "39";

/** Output-tax Kennzahlen: Haben increases them. All others are Vorsteuer, where Soll increases them. */
const OUTPUT_ROUTES: ReadonlySet<VatAccountRoute> = new Set(["65", "69"]);

/** Sondervorauszahlung: deducted once, in the December return. */
export const VAT_SONDERVORAUSZAHLUNG_ROUTE: VatAccountRoute = "39";

const routes = (entries: [VatAccountRoute, number[]][]): ReadonlyMap<number, VatAccountRoute> =>
    new Map(entries.flatMap(([route, accounts]) => accounts.map((account) => [account, route] as const)));

// PO mapping UStVA_Konten_v1, plus BU 6/7 (5 %/16 % Vorsteuer) and SKR03 BU 17 (1589), which the BU table routes to 66/61.
const VAT_ACCOUNT_ROUTES: Readonly<Record<number, ReadonlyMap<number, VatAccountRoute>>> = {
    3: routes([
        ["66", [1570, 1571, 1573, 1576, 1585, 1568, 1575]],
        ["61", [1572, 1574, 1584, 1589]],
        ["62", [1588]],
        ["67", [1577, 1578]],
        ["64", [1528, 1529, 1556, 1557, 1558, 1559]],
        ["65", [1782]],
        ["69", [1783, 1769, 1794]],
        ["39", [1781]],
    ]),
    4: routes([
        ["66", [1400, 1401, 1436, 1406, 1431, 1403, 1405]],
        ["61", [1402, 1404, 1432]],
        ["62", [1433]],
        ["67", [1407, 1408]],
        ["64", [1376, 1377, 1396, 1397, 1398, 1399]],
        ["65", [3832]],
        ["69", [3851, 3839, 3819]],
        ["39", [3830]],
    ]),
};

/**
 * The Kennzahl a booking line reaches through its account. Only Sachkonten match, on their
 * 4-digit base number, so extended numbers and sub-accounts inherit it while a Debitor such as
 * 14000 never does.
 */
export const getVatAccountRoute = (skr: number, konto: Model.IExtNum | undefined): VatAccountRoute | undefined => {
    if (!(konto instanceof Category)) {
        return undefined;
    }
    const base = Number(konto.num.substring(0, Category.DefaultLen));
    return VAT_ACCOUNT_ROUTES[skr]?.get(base);
};

/** Signed contribution of a line on a mapped account: Vorsteuer Soll +, output tax Haben +. */
export const getVatAccountAmount = (route: VatAccountRoute, isHaben: boolean, amount: number): number => {
    const isIncrease = OUTPUT_ROUTES.has(route) ? isHaben : !isHaben;
    return isIncrease ? amount : -amount;
};
